import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getEnv } from "@/config/env";

const API_BASE_URL = getEnv("VITE_API_BASE_URL");

/** Fetches an artifact's extracted content as an object URL.
 *
 *  Mirrors useDocumentFile, but points at the artifact file endpoint, which
 *  re-signs storage access per request. The stored download_url can't be used
 *  directly: it's a SAS that expires an hour after extraction, so anything
 *  opened the next day would fail.
 *
 *  Text content is returned as well as a URL, because a table is rendered as a
 *  grid rather than shown in an <img> or an iframe.
 *
 *  Only the most recent request is allowed to write. The preview dialog reuses
 *  one instance as the user moves between artifacts, so without this a slow
 *  response for the artifact they left could land after the one they opened,
 *  showing its content under the wrong title — and a response arriving after
 *  the dialog closed would create an object URL nothing would ever revoke. */
export function useArtifactFile() {
  const { getAccessToken } = useAuth();
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Identifies the request allowed to write. Bumping it retires whatever is in
  // flight, whether a new fetch starts, the content is cleared, or the
  // component goes away.
  const requestId = useRef(0);
  const controller = useRef<AbortController | null>(null);

  const retire = useCallback(() => {
    requestId.current += 1;
    controller.current?.abort();
    controller.current = null;
  }, []);

  const clearFile = useCallback(() => {
    retire();
    setBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setText(null);
    setError(null);
    setIsLoading(false);
  }, [retire]);

  useEffect(() => retire, [retire]);

  const fetchFile = useCallback(
    async (projectId: string, uid: string, asText = false, version?: string | null) => {
      retire();
      const id = requestId.current;
      const abort = new AbortController();
      controller.current = abort;

      setIsLoading(true);
      setError(null);
      setText(null);
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });

      try {
        const token = await getAccessToken();
        if (!token) throw new Error("Not authenticated");

        // The version is a cache key, not something the server reads. Content
        // is served with a long immutable cache, but re-extraction overwrites
        // the blob under the same name at the same URL — without this the
        // browser would keep serving the previous table or chart for a day
        // after someone uploaded a revised model.
        //
        // Callers pass updatedAt, which is a stand-in: it also changes on a
        // status transition or a description edit, so it refetches slightly
        // more than it needs to. artifact.version would be the honest key, but
        // it is hardcoded "v1" today and only gets real values in 7640 — and
        // even then it holds only if bytes can't change without it bumping,
        // which a re-extraction of an unchanged model version would break.
        const url =
          `${API_BASE_URL}/api/projects/${projectId}/artifacts/${encodeURIComponent(uid)}/file` +
          (version ? `?v=${encodeURIComponent(version)}` : "");

        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abort.signal,
        });
        if (!response.ok) throw new Error(`Failed to load artefact: ${response.status}`);

        const blob = await response.blob();
        // Checked after every await, not just before: a superseded request must
        // not create an object URL, because nothing is left holding it to
        // revoke it later.
        if (requestId.current !== id) return;

        if (asText) {
          const value = await blob.text();
          if (requestId.current !== id) return;
          setText(value);
        } else {
          setBlobUrl(URL.createObjectURL(blob));
        }
      } catch (err) {
        if (requestId.current !== id) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err.message : "Failed to load artefact");
      } finally {
        if (requestId.current === id) setIsLoading(false);
      }
    },
    [getAccessToken, retire]
  );

  return { blobUrl, text, isLoading, error, fetchFile, clearFile };
}
